import React, {useEffect, useState} from 'react';
import {onValue, ref} from 'firebase/database';
import { onValueThrottled } from '../utils/rtdbThrottle';
import {db} from '../firebase';
import {
  FiActivity, FiArrowRight,
  FiEdit2,
  FiLayers,
  FiLayout,
  FiMonitor,
  FiPieChart,
} from 'react-icons/fi';
import { macToSlug } from '../utils/macSlug';
import { PanelCard, StatCard } from '../components/DashboardCards';
import FleetHealthStrip from '../components/analytics/FleetHealthStrip';
import DeviceMap from '../components/analytics/DeviceMap';
import {toast, ToastContainer} from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {Pie} from 'react-chartjs-2';
import {ArcElement, Chart as ChartJS, Legend, Tooltip} from 'chart.js';
import {useNavigate} from 'react-router-dom'

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend);

export default function DashboardPage() {
  const navigate = useNavigate()

  const [devices, setDevices] = useState({});
  const [groupList, setGroupList] = useState({});
  const [appsList, setAppsList] = useState([]);
  const [otaInfo, setOtaInfo] = useState({});

  function compareVersions(a, b) {
    // Ensure both values are strings
    const strA = String(a || '0');
    const strB = String(b || '0');
    const pa = strA.split('.').map(n => parseInt(n, 10) || 0);
    const pb = strB.split('.').map(n => parseInt(n, 10) || 0);
    for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
      const na = pa[i] || 0;
      const nb = pb[i] || 0;
      if (na > nb) return 1;
      if (na < nb) return -1;
    }
    return 0;
  }

  useEffect(() => {
    const devRef = ref(db, 'mdm/devices');
    const groupListRef = ref(db, 'mdm/groups');
    const otaRef = ref(db, 'mdm/ota');
    const appRef = ref(db, 'mdm/app_store');

    const unsub1 = onValueThrottled(devRef, snap => {
      setDevices(snap.val() || {});
    });
    const unsub2 = onValue(groupListRef, snap => {
      setGroupList(snap.val() || {});
    });
    const unsub4 = onValue(appRef, snap => {
      setAppsList(snap.val() || {});
    });

    const unsub3 = onValue(otaRef, snap => {
      const ota = snap.val() || {};

      // single pass to find the highest version
      let latest = null;
      for (const [id, info] of Object.entries(ota)) {
        if (!info.version) continue;
        if (
            !latest ||
            compareVersions(info.version, latest.version) > 0
        ) {
          latest = { id, version: info.version, url: info.url };
        }
      }

      // update your state with that single “latest” entry
      setOtaInfo(latest || {});
    });

    return () => {
      unsub1();
      unsub2();
      unsub3();
      unsub4();
    };
  }, []);

  //Navigation function
  const handleNavigation = (path) => {
    navigate(path);
  }


  // Device Metrics

  const totalDevices = Object.keys(devices).length;
  const onlineDevices = Object.values(devices).filter(dev =>  dev.status === 'online').length;
  const offlineDevices = totalDevices - onlineDevices;
  const totalGroups = Object.keys(groupList).length;

  // Group Details
  const groupDetails = Object.keys(groupList).map(groupName => {
    const groupDevices = Object.values(devices).filter(dev => dev.group === groupName);
    return {
      name: groupName,
      deviceCount: groupDevices.length,
      onlineCount: groupDevices.filter(dev => dev.status === 'online').length,
    };
  });
  // App details
  const uniqueAppsCount = Object.values(devices)
      .flatMap(dev => dev.app_list || [])
      .map(app => app.appName)
      .filter((name, idx, arr) => arr.indexOf(name) === idx)
      .length;

  // Devices with notes — flatten the live `devices` snapshot into a list
  // of "device + latest note" rows, sorted by most-recently-edited so
  // operators see the hottest annotations first. We already have the
  // notes inline because the dashboard subscribes to the whole
  // `mdm/devices` tree; no extra Firebase round-trip needed.
  const devicesWithNotes = Object.entries(devices)
      .map(([mac, dev]) => {
          const noteEntries = dev?.notes ? Object.values(dev.notes) : [];
          if (!noteEntries.length) return null;
          const latest = noteEntries.reduce((acc, n) =>
              !acc || (n.timestamp || 0) > (acc.timestamp || 0) ? n : acc, null);
          return {
              mac,
              name: dev.name || dev.customer_name || mac,
              count: noteEntries.length,
              latestText: latest?.text || '',
              latestTimestamp: latest?.timestamp || 0,
          };
      })
      .filter(Boolean)
      .sort((a, b) => b.latestTimestamp - a.latestTimestamp);


  // Device Status Pie Chart Data — translucent green slice over a deep
  // grey "offline" wedge, matching the tinted-pill aesthetic used by the
  // rest of the dashboard (e.g. bg-green-500/15 status badges).
  const pieData = {
    labels: ['Online', 'Offline'],
    datasets: [
      {
        data: [onlineDevices, offlineDevices],
        backgroundColor: ['rgba(34, 197, 94, 0.6)', '#2a2a2a'],
        hoverBackgroundColor: ['rgba(34, 197, 94, 0.8)', '#3a3a3a'],
        borderColor: ['rgba(34, 197, 94, 0.9)', '#3a3a3a'],
        borderWidth: 1,
      },
    ],
  };

  const pieOptions = {
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          color: '#D1D5DB',
          font: {
            size: 14,
          },
        },
      },
      tooltip: {
        backgroundColor: '#1F2937',
        titleColor: '#D1D5DB',
        bodyColor: '#D1D5DB',
      },
    },
    maintainAspectRatio: false,
  };

  return (
      // Negative margins cancel MainLayout's px-6 py-8 wrapper so the
      // new background bleeds edge-to-edge while we evaluate it on this
      // page only. Inner padding restored on the same div.
      <div className="page-shell">
        <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="dark"
        />

      {/* Overview Cards — flat dark surfaces, single brand accent on the
          icon badge, big white number, muted subtitle. Matches the design
          language of the monitoring dashboard. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard
          icon={<FiMonitor />}
          title="Total Devices"
          value={totalDevices}
          subtitle="Across all groups"
          onClick={() => handleNavigation('/devices')}
        />
        <StatCard
          icon={<FiActivity />}
          title="Online Devices"
          value={onlineDevices}
          subtitle={`${((onlineDevices / totalDevices) * 100 || 0).toFixed(1)}% online`}
          subtitleTone="success"
          onClick={() => handleNavigation('/devices?status=online')}
        />
        <StatCard
          icon={<FiLayers />}
          title="Total Groups"
          value={totalGroups}
          subtitle="Managing devices"
          onClick={() => handleNavigation('/groups')}
        />
        <StatCard
          icon={<FiLayout />}
          title="Total Apps"
          value={uniqueAppsCount}
          subtitle="Across all devices"
          onClick={() => handleNavigation('/apps')}
        />
      </div>

      {/* Fleet health (MDM analytics) - the full view is its own page, /health. */}
      <FleetHealthStrip />

      {/* Active devices by country / city of their internet address (approximate). */}
      <DeviceMap />

      {/* Devices with Notes — single panel listing every device that
          has at least one operator note. Each row links straight to
          /devices/<slug>#notes so operators jump from the dashboard to
          the relevant device's Notes tab without needing the device
          list as a stepping stone. */}
      <div className="mb-6">
        <PanelCard icon={<FiEdit2 />} title="Devices with Notes">
          {devicesWithNotes.length === 0 ? (
              <p className="text-sm text-gray-500">No devices have notes yet.</p>
          ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
                {devicesWithNotes.map((row) => (
                    <div
                        key={row.mac}
                        onClick={() => handleNavigation(`/devices/${macToSlug(row.mac)}#notes`)}
                        className="card-inset card-inset-hover p-3 flex items-center justify-between gap-3"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold text-white truncate">{row.name}</p>
                          <span className="brand-pill text-[10px] font-medium px-2 py-0.5 rounded-full shrink-0">
                            {row.count} note{row.count === 1 ? '' : 's'}
                          </span>
                        </div>
                        <p className="text-xs text-gray-400 truncate mt-0.5" title={row.latestText}>
                          {row.latestText}
                        </p>
                        <p className="text-[11px] text-gray-500 mt-0.5">
                          {row.latestTimestamp ? new Date(row.latestTimestamp).toLocaleString() : ''} · {row.mac}
                        </p>
                      </div>
                      <FiArrowRight className="text-gray-500 shrink-0" />
                    </div>
                ))}
              </div>
          )}
        </PanelCard>
      </div>

      {/* Device Status Chart and Group Details — same chrome as the
          stat cards above so the page reads as one design system. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <PanelCard icon={<FiPieChart />} title="Device Status Breakdown">
          <div className="h-64">
            <Pie data={pieData} options={pieOptions} />
          </div>
        </PanelCard>

        <PanelCard icon={<FiLayers />} title="Group Details">
          <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
            {groupDetails.length > 0 ? (
                groupDetails.map(group => (
                    <div
                        key={group.name}
                        onClick={() => handleNavigation('/groups/' + group.name)}
                        className="card-inset card-inset-hover p-3 flex justify-between items-center"
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-white truncate">{decodeURIComponent(group.name)}</p>
                        <p className="text-xs text-gray-500">Total: {group.deviceCount} · Online: {group.onlineCount}</p>
                      </div>
                      <div className="w-12 h-12 shrink-0">
                        <Pie
                            data={{
                              labels: ['Online', 'Offline'],
                              datasets: [
                                {
                                  data: [group.onlineCount, group.deviceCount - group.onlineCount],
                                  backgroundColor: ['rgba(34, 197, 94, 0.6)', '#2a2a2a'],
                                  borderColor: ['rgba(34, 197, 94, 0.9)', '#3a3a3a'],
                                  borderWidth: 1,
                                },
                              ],
                            }}
                            options={{
                              plugins: {
                                legend: { display: false },
                                tooltip: { enabled: true },
                              },
                              maintainAspectRatio: false,
                            }}
                        />
                      </div>
                    </div>
                ))
            ) : (
                <p className="text-sm text-gray-500">No groups available.</p>
            )}
          </div>
        </PanelCard>
      </div>

      </div>
);
}
